/**
 * KlientenSchnellsuche - Schnellsuche im Header
 * Klientenname eintippen → direkt zur 'Akte auf einen Blick' springen
 * 
 * Features:
 * - Tastenkürzel: Ctrl+Shift+K öffnet die Suche
 * - Zuletzt angesehen werden oben angezeigt (mit Uhr-Icon)
 * - Favoriten werden danach angezeigt (mit Stern markiert)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sparkles, X, User, Star, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useTabs } from '@/contexts/TabContext';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Zeitangabe formatieren (vor X Min/Std/Tagen)
function formatTimeAgo(isoString) {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffStd = Math.floor(diffMs / 3600000);
  const diffTage = Math.floor(diffMs / 86400000);
  
  if (diffMin < 1) return 'gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min`;
  if (diffStd < 24) return `vor ${diffStd} Std`;
  if (diffTage === 1) return 'gestern';
  if (diffTage < 7) return `vor ${diffTage} Tagen`;
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

export default function KlientenSchnellsuche({ className = '', onOpenChange, alwaysShowDropdown = false }) {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [favoriten, setFavoriten] = useState([]);
  const [zuletztAngesehen, setZuletztAngesehen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(alwaysShowDropdown);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Zuletzt angesehen laden
  const loadZuletztAngesehen = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/zuletzt-angesehen`);
      if (res.ok) {
        const data = await res.json();
        setZuletztAngesehen(data || []);
      }
    } catch (error) {
      console.error('Zuletzt angesehen laden Fehler:', error);
    }
  }, []);

  // Favoriten laden
  const loadFavoriten = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/favoriten/klienten/details`);
      if (res.ok) {
        const data = await res.json();
        setFavoriten(data || []);
      }
    } catch (error) {
      console.error('Favoriten laden Fehler:', error);
    }
  }, []);

  useEffect(() => {
    loadZuletztAngesehen();
    loadFavoriten();
  }, [loadZuletztAngesehen, loadFavoriten]);

  // Global Keyboard Shortcut: Ctrl+Shift+K
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Notify parent when open state changes
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  // Suche durchführen - Favoriten werden immer oben angezeigt
  const search = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/netzwerk/suche?q=${encodeURIComponent(term)}&typ=klient`);
      const data = await res.json();
      const searchResults = data.results?.slice(0, 8) || [];
      
      // Favoriten IDs für schnelle Lookup
      const favIds = new Set(favoriten.map(f => f.id));
      
      // Sortieren: Favoriten zuerst
      const sorted = [...searchResults].sort((a, b) => {
        const aIsFav = favIds.has(a.id);
        const bIsFav = favIds.has(b.id);
        if (aIsFav && !bIsFav) return -1;
        if (!aIsFav && bIsFav) return 1;
        return 0;
      });
      
      setResults(sorted);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Schnellsuche Fehler:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [favoriten]);

  // Debounce Suche
  useEffect(() => {
    const timer = setTimeout(() => {
      search(query);
    }, 200);
    return () => clearTimeout(timer);
  }, [query, search]);

  // Click outside handler - disabled when alwaysShowDropdown is true
  useEffect(() => {
    if (alwaysShowDropdown) return; // Skip for mobile overlay
    
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [alwaysShowDropdown]);

  // Tastatur-Navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      goToAkteBlick(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  // Zur Akte auf einen Blick navigieren
  const goToAkteBlick = (klient) => {
    // Erst navigieren
    openTab(`/akten/${klient.id}/blick`, { title: `${klient.name} - Blick` });
    navigate(`/akten/${klient.id}/blick`);
    // Dann aufräumen
    setQuery('');
    setOpen(false);
    setResults([]);
    // Parent benachrichtigen (für mobile Overlay)
    onOpenChange?.(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Klient suchen → Blick..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className="pl-9 pr-8 w-full md:w-64 lg:w-80 h-9 text-sm bg-slate-50 border-slate-200 focus:bg-white"
          data-testid="schnellsuche-input"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults([]); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown mit Ergebnissen */}
      {open && (query.length >= 2 || results.length > 0 || (query.length === 0 && (zuletztAngesehen.length > 0 || favoriten.length > 0))) && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-96 overflow-auto"
          data-testid="schnellsuche-dropdown"
        >
          {/* Zuletzt angesehen - Nur wenn keine Suche aktiv */}
          {query.length < 2 && zuletztAngesehen.length > 0 && (
            <div className="py-1">
              <div className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 border-b border-slate-100 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Zuletzt angesehen
              </div>
              {zuletztAngesehen.slice(0, 5).map((klient) => (
                <button
                  key={`recent-${klient.id}`}
                  onClick={() => goToAkteBlick(klient)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-blue-50 transition-colors"
                  data-testid={`schnellsuche-recent-${klient.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-800 truncate">{klient.name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {klient.fallnummer ? `Az: ${klient.fallnummer}` : ''} 
                      {klient.fallnummer && klient.ort ? ' · ' : ''}
                      {klient.ort || ''}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-xs text-slate-400">
                    {formatTimeAgo(klient.angesehen_um)}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Favoriten-Bereich wenn keine Suche aktiv */}
          {query.length < 2 && favoriten.length > 0 && (
            <div className="py-1">
              <div className="px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 border-b border-amber-100 flex items-center gap-1">
                <Star className="w-3 h-3 fill-amber-500" />
                Favoriten
              </div>
              {favoriten.slice(0, 5).map((klient) => (
                <button
                  key={`fav-${klient.id}`}
                  onClick={() => goToAkteBlick(klient)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-blue-50 transition-colors"
                  data-testid={`schnellsuche-favorit-${klient.id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Star className="w-4 h-4 text-amber-600 fill-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-800 truncate">{klient.name}</p>
                    <p className="text-xs text-slate-500 truncate">{klient.subtext}</p>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1 text-blue-600">
                    <Sparkles className="w-4 h-4" />
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {loading ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              Suche...
            </div>
          ) : results.length > 0 ? (
            <div className="py-1">
              {results.map((klient, index) => {
                const isFavorit = favoriten.some(f => f.id === klient.id);
                return (
                  <button
                    key={klient.id}
                    onClick={() => goToAkteBlick(klient)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-blue-50 transition-colors ${
                      index === selectedIndex ? 'bg-blue-50' : ''
                    }`}
                    data-testid={`schnellsuche-result-${klient.id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isFavorit ? 'bg-amber-100' : 'bg-blue-100'}`}>
                      {isFavorit ? (
                        <Star className="w-4 h-4 text-amber-600 fill-amber-500" />
                      ) : (
                        <User className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-slate-800 truncate">{klient.name}</p>
                      <p className="text-xs text-slate-500 truncate">{klient.subtext}</p>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-1 text-blue-600">
                      <Sparkles className="w-4 h-4" />
                      <span className="text-xs hidden sm:inline">Blick</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : query.length >= 2 ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              Kein Klient gefunden
            </div>
          ) : null}
          
          <div className="px-3 py-2 border-t border-slate-100 text-xs text-slate-400">
            <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px]">Ctrl+Shift+K</kbd> Öffnen · 
            <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] ml-1">↑↓</kbd> Navigation · 
            <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] ml-1">Enter</kbd> Auswählen · 
            <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] ml-1">Esc</kbd> Schließen
          </div>
        </div>
      )}
    </div>
  );
}
